import type { Features } from '@shared/feature-toggles'
import type { WorkflowStep } from './signup-workflow'

type WorkflowInstanceValues = { [key: string]: any }
type WorkflowInstanceParam = Array<WorkflowStep> | { steps: Array<WorkflowStep>; values: WorkflowInstanceValues }

/**
 * This is a workflow instance that has state and can be used to advance the workflow.
 */
export class SignupWorkflowInstance {
    persistentFeatureFlags: Partial<Record<Features, boolean>>
    steps: Array<WorkflowStep> = []
    values: WorkflowInstanceValues = {}

    /**
     * Constructor either invoked by the Workflow definition or by the deserialization process
     * @param steps
     */
    constructor(steps: WorkflowInstanceParam, persitentFeatureFlags: Partial<Record<Features, boolean>> = {}) {
        // TODO why are we stringify-ing and then parsing?
        if (Array.isArray(steps)) {
            this.steps = JSON.parse(JSON.stringify(steps))
        } else {
            const parts = JSON.parse(JSON.stringify(steps))
            this.steps = parts.steps
            this.values = parts.values
        }

        this.persistentFeatureFlags = persitentFeatureFlags
        Object.entries(persitentFeatureFlags).forEach(([feature, enabled], _) => {
            // Must be toString due to FF logic type-checking flag values with ===.
            this.values[`feature${feature}`] = enabled.toString()
        })
    }

    /**
     * Not used at the moment
     * @returns {*}
     */
    fromIntegrationCatalog(): any {
        return this.steps[0]
    }

    /**
     * Completes a step, optionally with some inputs
     * @param step - the step's name
     * @param inputs - the step's inputs. An object with keys - the names of the inputs and matching values.
     */
    completeStep(step: string, inputs = {}) {
        for (const currentStep of this.steps.filter(s => !s.external)) {
            if (currentStep.state !== 'complete') {
                const currentStepRequiredInputNames = Object.entries(currentStep.inputs || {})
                    .filter(([_, v]) => v.required)
                    .map(([k, _]) => k)
                const currentStepProvidedInputsNames = Object.keys(inputs)
                const requiredPresent = currentStepRequiredInputNames.every(inputName => currentStepProvidedInputsNames.includes(inputName))
                const urlMatch = currentStep.url === step
                if (urlMatch && requiredPresent) {
                    this.values = Object.assign({}, this.values, inputs)
                    currentStep.state = 'complete'
                    return
                } else {
                    throw new Error('Either there is a step mismatch or required input parameters are missing')
                }
            }
        }
    }

    /**
     * Getting the next step's name
     * @returns next step name, if it exists.
     */
    nextStep(): string | undefined {
        for (const currentStep of this.steps.filter(s => !s.external)) {
            if (currentStep.state !== 'complete') {
                return currentStep.url
            }
        }
    }

    /**
     * Providing completion status
     * @returns If the workflow instance is completed or not.
     */
    isComplete(): boolean {
        return this.steps.filter(s => !s.external).every(s => s.state === 'complete')
    }
    /**
     * Retrieving the value of an input by name
     * @param inputName - name of the input
     * @returns {*} - value collected during one of the previous steps
     */
    getInput(inputName: string): unknown {
        return this.values[inputName]
    }

    /**
     * Internal method to serialize the state. This can be later used as a parameter to the constructor of the class
     * @returns {any}
     */
    serialize(): any {
        return JSON.parse(JSON.stringify({ persistentFeatureFlags: this.persistentFeatureFlags, steps: this.steps, values: this.values }))
    }
}

/**
 * Store the workflow instance's state
 * @param name - name of the key to use when storing the state
 * @param workflowInstance
 */
export const storeWorkflowInstance = (name: string, workflowInstance: SignupWorkflowInstance) => {
    localStorage.setItem(name, JSON.stringify(workflowInstance.serialize()))
}

/**
 * Retrieving the state and instantiating a workflow instance with it
 * @param name - name of the key used when storing the state
 * @returns The instance, or 'not found'
 */
export const getWorkflowInstance = (name: string) => {
    const item = localStorage.getItem(name)
    if (item) {
        const parsed: unknown = JSON.parse(item)
        let persistentFeatureFlags: Partial<Record<Features, boolean>> = {}

        if (typeof parsed == 'object' && parsed != null) {
            persistentFeatureFlags =
                'persistentFeatureFlags' in parsed ? (parsed.persistentFeatureFlags as Partial<Record<Features, boolean>>) : {}
        }

        return new SignupWorkflowInstance(parsed as WorkflowInstanceParam, persistentFeatureFlags)
    } else {
        return 'not found'
    }
}

/**
 * Retrieving the state and instantiating a workflow instance with it.
 * Type-safe edition. Used because migration the old function is a bit
 * more work than we would like.
 * @param name - name of the key used when storing the state
 * @returns The instance, or undefined
 */
export const safeGetWorkflowInstance = (name: string): SignupWorkflowInstance | undefined => {
    const result = getWorkflowInstance(name)
    return result instanceof SignupWorkflowInstance ? result : undefined
}

export const integrationStartUrl = (newUrl: string | null = null) => {
    const KEY = 'integrationStartUrl'

    if (newUrl) {
        localStorage.setItem(KEY, newUrl)
        return
    }
    return { url: localStorage.getItem(KEY), remove: () => localStorage.removeItem(KEY) }
}

export const removeWorkflowInstance = (name: string) => {
    localStorage.removeItem(name)
}
